import { ChangeEvent } from "react";
import { makeStyles } from "tss-react/mui";
import { MiActions, MiCheckbox, MiChip, MiIcon } from "@miplatform/components";
import { BUTTON_VARIANTS, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { IBidArea, IMaterialsResponse } from "@miplatform/types";
import { mdiHelpCircleOutline } from "@mdi/js";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { MaterialCard } from "../../../../../components/MaterialCard/MaterialCard";

export function MaterialResultCard({
  index,
  material,
  isSelected,
  handleSelectItem,
  isSuppliedByBuilder,
  setIsSuppliedByBuilder,
  checkedAreas,
  planAreas,
  handleCheckArea,
  handleCancelItem,
  handleAddItemToAreas,
}: BidMaterialResultCardProps) {
  const theme = useTheme();

  return (
    <>
      {index > 0 && <Divider sx={{ margin: theme.spacing(1, 0) }} />}
      <MaterialCard
        noCardStyle
        item={material}
        isSelected={isSelected}
        onClickCard={() => handleSelectItem(material)}
      />
      {isSelected && (
        <BulkApplyCard
          checkedAreas={checkedAreas}
          planAreas={planAreas}
          isSelected={isSelected}
          isSuppliedByBuilder={isSuppliedByBuilder}
          setIsSuppliedByBuilder={setIsSuppliedByBuilder}
          handleCheckArea={handleCheckArea}
          handleCancelItem={handleCancelItem}
          handleAddItemToAreas={handleAddItemToAreas}
        />
      )}
    </>
  );
}

function BulkApplyCard({
  checkedAreas,
  planAreas,
  isSelected,
  isSuppliedByBuilder,
  setIsSuppliedByBuilder,
  handleCheckArea,
  handleCancelItem,
  handleAddItemToAreas,
}: BulkApplyCardProps) {
  const { classes, theme } = useStyles();

  const isAreaSelected = (area: IBidArea) =>
    checkedAreas.some((c) => c.id === area.id);

  return (
    <Box className={classes.bulkApplyCard}>
      <Box className={classes.bulkApplyTitle}>
        <MiIcon
          path={mdiHelpCircleOutline}
          color={theme.palette.orange.main}
          size={0.8}
        />
        <Typography fontWeight={theme.fontWeight.semiBold}>
          Bulk Apply
        </Typography>
      </Box>
      <Typography variant={TYPOGRAPHY_VARIANTS.SUBTITLE2} color="grey.main">
        If you would like to apply this item to multiple areas, please select
        them below and select &quot;Add to Area(s)&quot;
      </Typography>
      <Box className={classes.chipContainer}>
        {planAreas.map((area) => {
          const selected = isAreaSelected(area);

          return (
            <MiChip
              key={area.id}
              label={area.label || area.areaTypeName || ""}
              size="small"
              onClick={() => handleCheckArea(area, !selected)}
              clickable
              sx={{
                borderRadius: 1,
                border: `1px solid ${theme.palette.primary.main}`,
                backgroundColor: selected && theme.palette.primary.medium,
                color: selected && theme.palette.common.white,
                "&:hover": {
                  backgroundColor: selected
                    ? theme.palette.primary.accent
                    : theme.palette.primary.light,
                },
              }}
            />
          );
        })}
      </Box>
      <Box>
        {isSelected && (
          <MiCheckbox
            label={
              <Typography sx={{ fontSize: theme.fontSize.medium }}>
                Builder Supplied
              </Typography>
            }
            handleChange={(e: ChangeEvent<HTMLInputElement>) => {
              setIsSuppliedByBuilder(e.target.checked);
            }}
            value={isSuppliedByBuilder}
            size="small"
            style={{ marginLeft: theme.spacing(-2) }}
          />
        )}
      </Box>
      <Box className={classes.actionsContainer}>
        <MiActions
          actions={[
            {
              name: "Cancel",
              size: "small",
              variant: BUTTON_VARIANTS.TEXT,
              onClick: handleCancelItem,
              className: classes.cancelBtn,
            },
            {
              name: "Add to Area(s)",
              size: "small",
              variant: BUTTON_VARIANTS.CONTAINED,
              onClick: handleAddItemToAreas,
              disabled: !checkedAreas.length,
            },
          ]}
        />
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  bulkApplyCard: {
    padding: theme.spacing(2),
    borderRadius: "0 0 4px 4px",
    backgroundColor: theme.palette.orange.light,
  },
  bulkApplyTitle: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  icon: {
    borderRadius: 4,
    backgroundColor: theme.palette.orange.medium,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5),
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0, 2, 0),
    flexWrap: "wrap",
  },
  actionsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
  },
  cancelBtn: {
    color: theme.palette.common.black,
    fontWeight: theme.fontWeight.semiBold,
  },
}));

interface BidMaterialResultCardProps {
  index: number;
  material: IMaterialsResponse;
  isSelected: boolean;
  handleSelectItem: (material: IMaterialsResponse) => void;
  isSuppliedByBuilder: boolean;
  setIsSuppliedByBuilder: (value: boolean) => void;
  checkedAreas: IBidArea[];
  planAreas: IBidArea[];
  handleCheckArea: (area: IBidArea, checked: boolean) => void;
  handleCancelItem: () => void;
  handleAddItemToAreas: () => void;
}

interface BulkApplyCardProps {
  checkedAreas: IBidArea[];
  planAreas: IBidArea[];
  isSelected: boolean;
  isSuppliedByBuilder: boolean;
  setIsSuppliedByBuilder: (value: boolean) => void;
  handleCheckArea: (area: IBidArea, checked: boolean) => void;
  handleCancelItem: () => void;
  handleAddItemToAreas: () => void;
}
